import React, { useState, useEffect } from 'react';
import connectionService from '../services/connectionService';
import { saveRentalProfiling, updateRentalProfiling } from '../services/rentalProfilingService';

import {
  FileText,
  CreditCard,
  Calendar,
  Check,
  User,
  Mail,
  Building2,
  CheckCircle,
  AlertCircle,
  Search,
  Phone,
  PoundSterlingIcon,
  DollarSign,
  List
} from "lucide-react";

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
    <div className="bg-blue-100 px-4 py-3 border-b border-gray-200 rounded-t-lg">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const FormRow = ({ label, children, required = false }) => (
  <div className="flex items-center mb-3">
    <label className="w-1/2 text-sm font-medium text-gray-700 pr-4">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="w-1/2">{children}</div>
  </div>
);

const TextInput = ({ value, onChange, placeholder = "", type = "text", ...props }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    {...props}
  />
);

const Select = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

const RadioGroup = ({ value, onChange, options, name }) => (
  <div className="flex space-x-4">
    {options.map((option) => (
      <label key={option.value} className="inline-flex items-center">
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
          className="form-radio h-4 w-4 text-blue-600"
        />
        <span className="ml-2">{option.label}</span>
      </label>
    ))}
  </div>
);

const RentalProfiling = ({
  selectedConnectionId,
  onSaveSuccess,
  onCancel,
  viewMode,
  initialData,
  isEditing = false
}) => {
  const [formData, setFormData] = useState({
    connection_id: selectedConnectionId,
     cashbackVisible: "",
    cashbackVisibleReason: "",
    linesTermination: "",
    linesTerminationReason: "",
    callsTermination: "",
    callsTerminationReason: "",
    currentLeaseBuyOuts: "",
    currentLeaseBuyOutsReason: "",
    mobileBuyouts: "",
    mobileBuyoutsReason: "",
    otherCosts: "",
    otherCostsReason: "",
    paymentProfile: "84",
    rentalDiscount: "0",
    cashDiscount: "0",
    networkCreditLength: "12",
    unitsLease: "3.13",
   
  });

  const [connections, setConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setFormData(prev => ({ ...prev, connection_id: selectedConnectionId }));
  }, [selectedConnectionId]);

  // Populate form data when initialData is provided (for editing)
  useEffect(() => {
  
    
    if (initialData && isEditing) {
     
      const newFormData = {
        connection_id: selectedConnectionId,
        cashbackVisible: initialData.cashback_visible?.toString() || "",
        cashbackVisibleReason: initialData.cashback_visible_reason || "",
        linesTermination: initialData.lines_termination?.toString() || "",
        linesTerminationReason: initialData.lines_termination_reason || "",
        callsTermination: initialData.calls_termination?.toString() || "",
        callsTerminationReason: initialData.calls_termination_reason || "",
        currentLeaseBuyOuts: initialData.current_lease_buy_outs?.toString() || "",
        currentLeaseBuyOutsReason: initialData.current_lease_buy_outs_reason || "",
        mobileBuyouts: initialData.mobile_buyouts?.toString() || "",
        mobileBuyoutsReason: initialData.mobile_buyouts_reason || "",
        otherCosts: initialData.other_costs?.toString() || "",
        otherCostsReason: initialData.other_costs_reason || "",
        paymentProfile: initialData.payment_profile?.toString() || "84",
        rentalDiscount: initialData.rental_discount?.toString() || "0",
        cashDiscount: initialData.cash_discount?.toString() || "0",
        networkCreditLength: initialData.network_credit_length?.toString() || "12",
        unitsLease: initialData.units_lease?.toString() || "3.13",
        
      };
      
      
      setFormData(newFormData);
    }
  }, [initialData, isEditing, selectedConnectionId]);

  // Populate form data when initialData is provided (for editing)
  useEffect(() => {
    if (initialData && isEditing) {
      
      setFormData({
        connection_id: selectedConnectionId,
        cashbackVisible: initialData.cashback_visible?.toString() || "",
        cashbackVisibleReason: initialData.cashback_visible_reason || "",
        linesTermination: initialData.lines_termination?.toString() || "",
        linesTerminationReason: initialData.lines_termination_reason || "",
        callsTermination: initialData.calls_termination?.toString() || "",
        callsTerminationReason: initialData.calls_termination_reason || "",
        currentLeaseBuyOuts: initialData.current_lease_buy_outs?.toString() || "",
        currentLeaseBuyOutsReason: initialData.current_lease_buy_outs_reason || "",
        mobileBuyouts: initialData.mobile_buyouts?.toString() || "",
        mobileBuyoutsReason: initialData.mobile_buyouts_reason || "",
        otherCosts: initialData.other_costs?.toString() || "",
        otherCostsReason: initialData.other_costs_reason || "",
        paymentProfile: initialData.payment_profile?.toString() || "84",
        rentalDiscount: initialData.rental_discount?.toString() || "0",
        cashDiscount: initialData.cash_discount?.toString() || "0",
      
        networkCreditLength: initialData.network_credit_length?.toString() || "12",
        unitsLease: initialData.units_lease?.toString() || "3.13",
   
        
      });
    }
  }, [initialData, isEditing, selectedConnectionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const connectionsResponse = await connectionService.getAllConnections();
      const connectionsData = Array.isArray(connectionsResponse.data) ? connectionsResponse.data : [];
      setConnections(connectionsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateNumericInput = (value, allowNegative = false) => {
    if (value === "") return true;
    const regex = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    return regex.test(value);
  };

  const saveProfile = async () => {
    const numericFields = [
      'rentalDiscount', 'cashDiscount', 'unitsLease', 'sgpCashDeal',
      'cashbackVisible', 'linesTermination', 'callsTermination',
      'currentLeaseBuyOuts', 'mobileBuyouts', 'otherCosts'
    ];

    const processedData = {
      ...formData,
    };

    // Process numeric fields
    numericFields.forEach(field => {
      processedData[field] = processedData[field] === "" ? 
        null : 
        parseFloat(processedData[field]);
    });

    const connectionId = selectedConnectionId?.selectedConnectionId || selectedConnectionId;

    // Prepare data for API
    const apiData = {
      connection_id: connectionId,
      cashback_visible: processedData.cashbackVisible,
      cashback_visible_reason: processedData.cashbackVisibleReason,
      lines_termination: processedData.linesTermination,
      lines_termination_reason: processedData.linesTerminationReason,
      calls_termination: processedData.callsTermination,
      calls_termination_reason: processedData.callsTerminationReason,
      current_lease_buy_outs: processedData.currentLeaseBuyOuts,
      current_lease_buy_outs_reason: processedData.currentLeaseBuyOutsReason,
      mobile_buyouts: processedData.mobileBuyouts,
      mobile_buyouts_reason: processedData.mobileBuyoutsReason,
      other_costs: processedData.otherCosts,
      other_costs_reason: processedData.otherCostsReason,
      payment_profile: processedData.paymentProfile,
      rental_discount: processedData.rentalDiscount,
      cash_discount: processedData.cashDiscount,
      network_credit_length: processedData.networkCreditLength,
      units_lease: processedData.unitsLease,
    };

    try {
      let response;
      
      if (isEditing && initialData?.id) {
        // Update existing record
        //console.log('Updating rental profile with ID:', initialData.id);
        response = await updateRentalProfiling(initialData.id, apiData);
      } else {
        // Create new record
       // console.log('Creating new rental profile');
        response = await saveRentalProfiling(apiData);
      }
      
      alert(isEditing ? 'Updated successfully!' : 'Saved successfully!');
      
      // Prepare the data to return to parent component
      const savedData = {
        ...processedData,
        id: response.data?.id || initialData?.id, // Use response ID or existing ID
        connection_id: connectionId
      };
      
      // Call onSaveSuccess with the saved data
      if (onSaveSuccess) {
        onSaveSuccess(savedData);
      }
      
      return savedData;
    } catch (err) {
      console.error('Error saving:', err);
      alert(`Error ${isEditing ? 'updating' : 'saving'} data: ${err.message || 'Unknown error'}`);
      throw err;
    }
  };

  const filteredConnections = connections.filter(connection => {
    const matchesSearch = [
      connection.company_name,
      connection.company_reg,
      connection.md_name,
      connection.director_email
    ].some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const formatDiscountDisplay = (value) => {
    if (value === "" || value === null) return "£0";
    const num = parseFloat(value);
    if (isNaN(num)) return "£0";
    return num < 0 ? `-£${Math.abs(num)}` : `+£${num}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Cashback Section */}
      <Section title="Cashback" icon={DollarSign}>
        <div className="mb-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Description</th>
                <th className="border border-gray-300 p-2 text-left">Value</th>
                <th className="border border-gray-300 p-2 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2">Cashback visible to customer</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.cashbackVisible}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("cashbackVisible", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.cashbackVisibleReason}
                    onChange={(value) => handleInputChange("cashbackVisibleReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Other Costs Section */}
      <Section title="Other Costs" icon={List}>
        <div className="mb-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Description</th>
                <th className="border border-gray-300 p-2 text-left">Value</th>
                <th className="border border-gray-300 p-2 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2">Lines Termination Charges</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.linesTermination}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("linesTermination", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.linesTerminationReason}
                    onChange={(value) => handleInputChange("linesTerminationReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Calls Termination Charges</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.callsTermination}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("callsTermination", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.callsTerminationReason}
                    onChange={(value) => handleInputChange("callsTerminationReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Current Lease/Rental Buy Outs</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.currentLeaseBuyOuts}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("currentLeaseBuyOuts", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.currentLeaseBuyOutsReason}
                    onChange={(value) => handleInputChange("currentLeaseBuyOutsReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Mobile Buyouts</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.mobileBuyouts}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("mobileBuyouts", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.mobileBuyoutsReason}
                    onChange={(value) => handleInputChange("mobileBuyoutsReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">Other</td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    type="text"
                    value={formData.otherCosts}
                    onChange={(value) => {
                      if (validateNumericInput(value)) {
                        handleInputChange("otherCosts", value);
                      }
                    }}
                    placeholder="Enter value"
                  />
                </td>
                <td className="border border-gray-300 p-2">
                  <TextInput
                    value={formData.otherCostsReason}
                    onChange={(value) => handleInputChange("otherCostsReason", value)}
                    placeholder="Enter reason"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Payment Profile Section */}
      {/*
      <Section title="Payment Profile" icon={PoundSterlingIcon}>
        <FormRow label="Payment Profile:">
          <Select
            value={formData.paymentProfile}
            onChange={(value) => handleInputChange("paymentProfile", value)}
            options={[
              { value: "84", label: "84 months" },
              { value: "60", label: "60 months" },
              { value: "36", label: "36 months" },
              { value: "24", label: "24 months" },
              { value: "12", label: "12 months" },
            ]}
          />
        </FormRow>

        <FormRow label="Rental Discount:">
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="mr-2 text-gray-500">£</span>
              <TextInput
                type="text"
                value={formData.rentalDiscount}
                onChange={(value) => {
                  if (validateNumericInput(value, true)) {
                    handleInputChange("rentalDiscount", value);
                  }
                }}
                placeholder="e.g., -150 or 100"
              />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formData.rentalDiscount ? (
                <span>
                  This will {formData.rentalDiscount.startsWith('-') ? 'reduce ' : 'increase '}  
                   the lease by £{Math.abs(parseFloat(formData.rentalDiscount)) || 0}
                </span>
              ) : (
                "Enter negative value to reduce lease or positive to increase"
              )}
            </div>
          </div>
        </FormRow>

        <FormRow label="Cash Discount:">
          <div className="flex items-center">
            <span className="mr-2 text-gray-500">£</span>
            <TextInput
              type="text"
              value={formData.cashDiscount}
              onChange={(value) => {
                if (validateNumericInput(value, true)) {
                  handleInputChange("cashDiscount", value);
                }
              }}
              placeholder="Enter amount"
            />
          </div>
        </FormRow>
      </Section>*/}

      {/* Network Credit Section */}
      {/* 
      <Section title="Network Credit" icon={CreditCard}>
        <FormRow label="Network Credit Length:">
          <Select
            value={formData.networkCreditLength}
            onChange={(value) => handleInputChange("networkCreditLength", value)}
            options={[
              { value: "12", label: "12 months" },
              { value: "6", label: "6 months" },
            ]}
          />
        </FormRow>
      </Section>*/}

      {/* Submit Button */}
      <div className="flex justify-center pt-6 space-x-4">
        <button 
          onClick={saveProfile} 
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-200 flex items-center gap-2"
        >
          <Check className="w-5 h-5" />
          {isEditing ? 'Update Rental Profile' : 'Save Rental Profile'}
        </button>
        
        {onCancel && (
          <button 
            onClick={onCancel} 
            className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-200"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default RentalProfiling;